import groupBy from 'lodash/groupBy';
import values from 'lodash/values';
import concat from 'lodash/concat';
import get from 'lodash/get';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {
  hasDeliveryDetails,
  IOrder,
  IOrderDelivery,
  IOrderLine,
  IPaymentIntent,
  ngHelper,
  ORDER_LINE_STATES,
} from '@cakeiteasy/web-utils';
import { CART_DELIVERY_TYPES, CART_LINE_TYPES, DELIVERY_ORDER_DELIVERY_TYPES } from '../cart';
import { RETURN_METHODS } from '../return-packaging/models/return-packaging.constants';
import { getReturnPackagingOrderLine } from '../return-packaging/policy/return-packaging.policy';
import { IReturnPackagingOrderLine } from '../return-packaging/models/i-return-packaging-details';

@Component({
  standalone: false,
  selector: 'unified-order-info-table',
  templateUrl: './unified-order-info-table.component.html',
  styleUrls: ['./unified-order-info-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UnifiedOrderInfoTableComponent implements OnChanges {
  @Input() paymentIntent: IPaymentIntent;
  @Input() order: IOrder;
  @Input() isCustomerView = true;
  @Input() showHeader = true;
  @Input() showDeliveryFooter = false;
  @Input() showDeliveryEdit = false;
  @Output() onShowMap = new EventEmitter<void>();
  @Output() onDeliveryEdit = new EventEmitter<void>();

  orderLines: IOrderLine[];
  isPaymentIntentView: boolean;
  bakeryCurrency: { total: string; symbol: string };
  invoiceFeeOrderLine: IOrderLine = null;
  deliveryOrder: IOrderDelivery;
  deliveryPrice: number = null;
  returnPackagingOrderLine: IReturnPackagingOrderLine = null;
  hasDeliveryDetails = false;

  CART_LINE_TYPES = CART_LINE_TYPES;
  RETURN_METHODS = RETURN_METHODS;
  CART_DELIVERY_TYPES = CART_DELIVERY_TYPES;
  ORDER_LINE_STATES = ORDER_LINE_STATES;

  ngOnChanges(changes: SimpleChanges): void {
    ngHelper.ifChanged(changes, 'paymentIntent', (paymentIntent: IPaymentIntent) => {
      if (paymentIntent && paymentIntent.order) {
        this.order = paymentIntent.order;
        this.isPaymentIntentView = true;
      }
    });

    if (this.order) {
      this._deriveViewState();
    }
  }

  private _deriveViewState(): void {
    const currencySymbol = get(this.order, 'bakery.country.currencySymbol');

    this.deliveryOrder = this.order.deliveryOrder;
    this.hasDeliveryDetails = hasDeliveryDetails(this.deliveryOrder);
    this.bakeryCurrency = { total: currencySymbol, symbol: currencySymbol };
    this.orderLines = this.filterOrderLines(this._orderExtraProductsByType(this.order.orderLines));
    this.invoiceFeeOrderLine = this._getInvoiceFeeOrderLine();
    this.deliveryPrice = this._getDeliveryPrice();
    this.returnPackagingOrderLine = getReturnPackagingOrderLine(this.order.orderLines);
  }

  private _getDeliveryPrice() {
    if (!this.hasDeliveryDetails) {
      return null;
    }

    const { GIFT, THIRD_PARTY } = DELIVERY_ORDER_DELIVERY_TYPES;
    const { DELIVERY_DEPOSIT } = CART_LINE_TYPES;
    const {
      multiOrder: { orderLines },
      deliveryOrder: { deliveryType, price: deliveryOrderPrice, externalDeliveryFee },
    } = this.order;

    if (deliveryType === GIFT && this.isCustomerView) {
      return this.getLinePriceByType(DELIVERY_DEPOSIT, orderLines) || deliveryOrderPrice;
    }

    if (deliveryType === THIRD_PARTY) {
      return externalDeliveryFee;
    }

    return deliveryOrderPrice;
  }

  private getLinePriceByType(type: string, lines: IOrderLine[]): number {
    return lines.filter((l) => l.type === type).reduce((acc, l) => ((acc = acc + l.price), acc), 0);
  }

  private filterOrderLines(orderLines: IOrderLine[]): IOrderLine[] {
    const { INVOICE_FEE, REFUND, DISCOUNT, THIRD_PARTY_DELIVERY_FEE, THIRD_PARTY_DELIVERY_SUBSIDY, RETURN_PACKAGING } =
      CART_LINE_TYPES;
    const excludedTypes = [
      INVOICE_FEE,
      REFUND,
      DISCOUNT,
      THIRD_PARTY_DELIVERY_FEE,
      THIRD_PARTY_DELIVERY_SUBSIDY,
      RETURN_PACKAGING,
    ];

    return orderLines.filter(({ type }) => excludedTypes.indexOf(type) === -1);
  }

  private _orderExtraProductsByType(orderLines) {
    const { EXTRA_PRODUCT, GROUPED_EXTRA_PRODUCTS } = CART_LINE_TYPES;

    return orderLines.map((oLine) => {
      const otherProducts = oLine.orderLines.filter(
        (l) => l.type !== EXTRA_PRODUCT || (l.type === EXTRA_PRODUCT && !get(l, 'details.extraGroup.id')),
      );

      const extraProductLines = oLine.orderLines.filter(
        (line) => line.type === EXTRA_PRODUCT && Boolean(get(line, 'details.extraGroup.id')),
      );

      const extraProducts = values(groupBy(extraProductLines, (product) => product.details.extraGroup.id)).map(
        (productsByGroup) => ({
          trackId: `extra-group-${get(productsByGroup[0], 'details.extraGroup.id')}`,
          type: GROUPED_EXTRA_PRODUCTS,
          products: productsByGroup,
          name: productsByGroup[0] ? get(productsByGroup[0], 'details.extraGroup.name') : '',
        }),
      );

      return Object.assign({}, oLine, { orderLines: concat(extraProducts, otherProducts) });
    });
  }

  private _getInvoiceFeeOrderLine() {
    const {
      multiOrder: { orderLines: multiOrderLines },
      orderLines,
    } = this.order;
    const bakeryInvoiceFee = orderLines.find((l) => l.type === CART_LINE_TYPES.INVOICE_FEE) || null;

    if (this.isCustomerView) {
      const cakeItEasyInvoiceFee = multiOrderLines.find((l) => l.type === CART_LINE_TYPES.INVOICE_FEE) || null;
      return bakeryInvoiceFee || cakeItEasyInvoiceFee;
    }

    return bakeryInvoiceFee;
  }
}
